import React, { useState } from 'react';
import { Card, Spinner, Row, Col } from '@/components/ui';
import { formatDollars } from '../../../utils/formatDollars';
import './AggregationCard.css';

export interface AggregationTotals {
  totalExpectedAccomplishment: number;
  totalActualAccomplishment: number;
  totalExpectedRevenue: number; // This will be renamed to "Total Planned Revenue" in the UI
  totalActualValue: number;
  totalCosts: number;
}

interface AggregationCardProps {
  totals: AggregationTotals;
  loading?: boolean;
  /**
   * Only the activity selection is read (to decide whether a single-activity quantity makes
   * sense). The rest is accepted for context; each field takes a list because the QP Tracking
   * filters are multiselects, and a bare string because deep links can still supply one.
   */
  filters?: {
    serviceArea: string | string[];
    foremanArea: string[];
    contractYear: string | string[];
    activityNumber?: string[];
    activityName?: string[]; // Backward compatibility for older callers
    projectId: string | string[];
    status: string[];
  };
}

const AggregationCard: React.FC<AggregationCardProps> = ({ totals, loading = false, filters }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const wholeDollarOpts = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Check if exactly one activity is selected
  const selectedActivityCount =
    filters?.activityNumber?.length ?? filters?.activityName?.length ?? 0;
  const shouldShowQuantityAccomplishment = selectedActivityCount === 1;
  
  // Calculate profit as Total Actual Value - Total Costs
  const profit = totals.totalActualValue - totals.totalCosts;

  if (loading) {
    return (
      <Card className="tw-mb-3 aggregation-card">
        <Card.Body className="aggregation-loading">
          <div className="tw-flex tw-justify-between tw-items-center">
            <h6 className="tw-mb-0">
              <i className="bi bi-graph-up tw-mr-2"></i>
              Summary Totals
            </h6>
            <Spinner size="sm">Loading...</Spinner>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="tw-mb-3 aggregation-card">
      <Card.Body
        className="aggregation-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="tw-flex tw-justify-between tw-items-center">
          <h6 className="tw-mb-0">
            <i className="bi bi-graph-up tw-mr-2 tw-text-primary"></i>
            Summary Totals
          </h6>
          <div className="tw-flex tw-items-center">
            <span className="tw-text-content-muted tw-mr-2 tw-text-sm">
              {isExpanded ? 'Click to collapse' : 'Click to expand'}
            </span>
            <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} tw-text-primary`}></i>
          </div>
        </div>


      </Card.Body>

      {isExpanded && (
        <Card.Body className="tw-border-t tw-border-line tw-bg-surface-alt collapse-transition">
          <Row>
            {/* Only show quantity/accomplishments if exactly one activity is filtered */}
            {shouldShowQuantityAccomplishment && (
              <Col lg={3} md={6}>
                <div className="tw-h-full tw-rounded tw-bg-primary/10 aggregation-metric">
                  <div className="tw-p-4 tw-text-center">
                    <i className="bi bi-clipboard-check metric-icon tw-text-primary"></i>
                    <h6 className="tw-mb-2 tw-text-primary">Total Accomplishment</h6>
                    <div className="tw-mb-2">
                      <div className="tw-text-sm tw-text-content-muted">Planned:</div>
                      <div className="metric-value tw-text-primary tw-mb-1">
                        {formatNumber(totals.totalExpectedAccomplishment)}
                      </div>
                    </div>
                    <div className="tw-mb-2">
                      <div className="tw-text-sm tw-text-content-muted">Actual:</div>
                      <div className="metric-value tw-text-primary tw-mb-1">
                        {formatNumber(totals.totalActualAccomplishment)}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            )}

            {/* Total Value card with Planned and Actual */}
            <Col lg={shouldShowQuantityAccomplishment ? 3 : 4} md={6}>
              <div className="tw-h-full tw-rounded tw-bg-success/10 aggregation-metric">
                <div className="tw-p-4 tw-text-center">
                  <i className="bi bi-cash-coin metric-icon tw-text-success"></i>
                  <h6 className="tw-mb-2 tw-text-success">Total Revenue</h6>
                  <div className="tw-mb-2">
                    <div className="tw-text-sm tw-text-content-muted">Planned:</div>
                    <div className="metric-value tw-text-success tw-mb-1">
                      {formatDollars(totals.totalExpectedRevenue, wholeDollarOpts)}
                    </div>
                  </div>
                  <div className="tw-mb-2">
                    <div className="tw-text-sm tw-text-content-muted">Actual:</div>
                    <div className="metric-value tw-text-success tw-mb-1">
                      {formatDollars(totals.totalActualValue, wholeDollarOpts)}
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            {/* Total Costs */}
            <Col lg={shouldShowQuantityAccomplishment ? 3 : 4} md={6}>
              <div className="tw-h-full tw-rounded tw-bg-danger/10 aggregation-metric">
                <div className="tw-p-4 tw-text-center">
                  <i className="bi bi-tags metric-icon tw-text-danger"></i>
                  <h6 className="tw-mb-2 tw-text-danger">Total Costs</h6>
                  <h4 className="metric-value tw-text-danger">
                    {formatDollars(totals.totalCosts, wholeDollarOpts)}
                  </h4>
                </div>
              </div>
            </Col>

            {/* Profit (Actual Value - Costs) */}
            <Col lg={shouldShowQuantityAccomplishment ? 3 : 4} md={6}>
              <div className={`tw-h-full tw-rounded aggregation-metric ${profit >= 0 ? 'net-difference-positive' : 'net-difference-negative'}`}>
                <div className="tw-p-4 tw-text-center">
                  <i className={`bi ${profit >= 0 ? 'bi-graph-up-arrow' : 'bi-graph-down-arrow'} metric-icon ${profit >= 0 ? 'tw-text-success' : 'tw-text-danger'}`}></i>
                  <h6 className="tw-mb-2">Profit</h6>
                  <h4 className={`metric-value ${profit >= 0 ? 'tw-text-success' : 'tw-text-danger'}`}>
                    {formatDollars(profit, wholeDollarOpts)}
                  </h4>
                  <div className="metric-description">
                    Actual Revenue minus Costs
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <div className="tw-mt-3 tw-text-center">
            <small className="tw-text-content-muted">
              <i className="bi bi-info-circle tw-mr-1"></i>
              Totals are calculated based on all tasks matching your current filters, not just the visible page.
              <br />
              <i className="bi bi-exclamation-triangle tw-mr-1"></i>
              Tasks with 'CANCELLED' status are excluded from all aggregation calculations.
              {shouldShowQuantityAccomplishment ? (
                <>
                  <br />
                  <i className="bi bi-check-circle tw-mr-1"></i>
                  Quantity/accomplishment totals shown because exactly one activity is filtered.
                </>
              ) : (
                <>
                  <br />
                  <i className="bi bi-lightbulb tw-mr-1"></i>
                  Select a single activity to summarize Accomplishment quantities.
                </>
              )}
            </small>
          </div>
        </Card.Body>
      )}
    </Card>
  );
};

export default AggregationCard;